// src/components/ArtifactRenderer.jsx
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Copy } from "lucide-react";
import { copyForWord } from "../utils/copyForWord";
import {
  PiBriefcaseLight,
  PiCurrencyDollarLight,
  PiLaptopLight,
  PiRulerLight,
  PiAtomLight,
  PiHandshakeLight,
  PiScalesLight,
  PiBookOpenLight,
  PiMaskHappyLight,
  PiStethoscopeLight,
  PiHeartbeatLight,
  PiShieldCheckLight,
  PiChefHatLight,
  PiBroomLight,
  PiHandHeartLight,
  PiShoppingCartLight,
  PiOfficeChairLight,
  PiPlantLight,
  PiHardHatLight,
  PiWrenchLight,
  PiFactoryLight,
  PiTruckLight,
  PiTargetLight,
} from 'react-icons/pi';

// your existing visual components
import FlexibleBarChart    from "./FlexibleBarChart";
import FlexibleLineChart   from "./FlexibleLineChart";
import EquityNivoBarChart  from "./EquityNivoBarChart";
import DemandHoverTooltip   from "./DemandHoverTooltip";
import LinksPopover         from "./LinksPopover";
import CircularRating       from "./CircularRating";
import AnnualWageWithRating from "./AnnualWageWithRating";
import ImprovedMarkdownRenderer from "../chatGPT/components/ImprovedMarkdownRenderer";
import { ProductsContext }  from "../context/products.context";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import { getMaxDemandForWDA } from "../utils/demandUtils";

// Icon mapping for SOC major groups
const iconMapping = {
  "11": PiBriefcaseLight,
  "13": PiCurrencyDollarLight,
  "15": PiLaptopLight,
  "17": PiRulerLight,
  "19": PiAtomLight,
  "21": PiHandshakeLight,
  "23": PiScalesLight,
  "25": PiBookOpenLight,
  "27": PiMaskHappyLight,
  "29": PiStethoscopeLight,
  "31": PiHeartbeatLight,
  "33": PiShieldCheckLight,
  "35": PiChefHatLight,
  "37": PiBroomLight,
  "39": PiHandHeartLight,
  "41": PiShoppingCartLight,
  "43": PiOfficeChairLight,
  "45": PiPlantLight,
  "47": PiHardHatLight,
  "49": PiWrenchLight,
  "51": PiFactoryLight,
  "53": PiTruckLight,
  "55": PiTargetLight,
};

// Short names for display
const shortMajorMapping = {
  "11": "Management",
  "13": "Business/Finance",
  "15": "Computer/Math",
  "17": "Architecture/Eng",
  "19": "Sciences",
  "21": "Social Service",
  "23": "Legal",
  "25": "Education",
  "27": "Arts/Media",
  "29": "Healthcare",
  "31": "Healthcare Support",
  "33": "Protective Service",
  "35": "Food Service",
  "37": "Building/Grounds",
  "39": "Personal Service",
  "41": "Sales",
  "43": "Office/Admin",
  "45": "Farming/Forestry",
  "47": "Construction",
  "49": "Installation/Repair",
  "51": "Production",
  "53": "Transportation",
  "55": "Military",
};

/* ------------------------------------------------------------------ */
/* Helper: generic card wrapper with header / footer                  */
/* ------------------------------------------------------------------ */
const Card = ({ color, title, children, footer }) => {
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  // Copy as formatted HTML for MS Word (portrait 8.5x11 optimized)
  const onCopy = async () => {
    if (!contentRef.current) return;
    const success = await copyForWord(contentRef.current, title);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className={`border ${color.border} rounded-lg overflow-hidden my-6 shadow-sm max-w-full`}>
      {/* header */}
      <div className={`${color.bg} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <h3 className={`font-semibold ${color.text}`}>{title}</h3>
        </div>
        <button
          onClick={onCopy}
          className={`${color.text} hover:opacity-70 transition-opacity`}
          title="Copy"
        >
          {copied ? "✓" : <Copy size={16} />}
        </button>
      </div>

      {/* body */}
      <div ref={contentRef} className="bg-white p-4">
        {children}
      </div>

      {/* footer */}
      {footer && (
        <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-t">
          {footer}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main ArtifactRenderer                                              */
/* ------------------------------------------------------------------ */

// Module-level WDA cache — survives component mount/unmount cycles.
// First fetch of a WDA populates this; subsequent renders read synchronously.
const _wdaCache = new Map();

function getOrFetchWDA(wdaName) {
  if (_wdaCache.has(wdaName)) return { data: _wdaCache.get(wdaName), loading: false };
  return { data: null, loading: true };
}

export default function ArtifactRenderer({ artifact }) {
  // Get context at top level BEFORE any early returns (hooks can't be called conditionally)
  const { jobs: contextJobs } = useContext(ProductsContext);

  // Direct WDA fetch: when the artifact includes a wda field, fetch that WDA's
  // data from Firebase directly (same pattern as SOC codes — artifact is self-contained).
  // Module-level cache ensures second+ renders for the same WDA are instant.
  // Legacy artifacts without a wda field fall back to contextJobs from session.
  const artifactWDA = artifact?.artType === 'table/wa-labor-market' ? artifact?.data?.wda : null;
  const cached = artifactWDA ? getOrFetchWDA(artifactWDA) : { data: null, loading: false };
  const [wdaJobs, setWdaJobs] = useState(cached.data);
  const [wdaLoading, setWdaLoading] = useState(cached.loading);

  useEffect(() => {
    if (!artifactWDA) {
      setWdaJobs(null);
      setWdaLoading(false);
      return;
    }

    // Already cached — sync into state immediately (handles chat switches)
    if (_wdaCache.has(artifactWDA)) {
      setWdaJobs(_wdaCache.get(artifactWDA));
      setWdaLoading(false);
      return;
    }

    let cancelled = false;
    setWdaLoading(true);
    async function fetchWDA() {
      try {
        const snap = await getDoc(doc(db, "LMI26_WA", artifactWDA));
        if (cancelled) return;
        if (snap.exists()) {
          const rawData = snap.data();
          let enriched = rawData;
          if (Array.isArray(rawData.socData)) {
            enriched = {
              ...rawData,
              socData: rawData.socData.map((job) => ({
                ...job,
                maxDemand: getMaxDemandForWDA(job.onetSOC, artifactWDA),
              })),
            };
          }
          _wdaCache.set(artifactWDA, enriched);
          setWdaJobs(enriched);
        }
      } catch (err) {
        console.warn('[ArtifactRenderer] WDA fetch failed:', err);
      } finally {
        if (!cancelled) setWdaLoading(false);
      }
    }
    fetchWDA();
    return () => { cancelled = true; };
  }, [artifactWDA]);

  // Resolve jobs for wa-labor-market table (used only in that case)
  // This must be called before early return
  const resolvedJobs = useMemo(() => {
    if (!artifact || artifact.artType !== 'table/wa-labor-market') return [];

    const { data = {} } = artifact;

    // If jobs array is provided, use it directly (oldest artifact format)
    if (data.jobs && data.jobs.length > 0) {
      return data.jobs;
    }

    // Pick job source: artifact-specified WDA (new) or session context (legacy fallback)
    const jobSource = artifactWDA ? wdaJobs : contextJobs;

    // If socCodes provided, filter from the appropriate job source
    if (data.socCodes && data.socCodes.length > 0 && jobSource?.socData) {
      const filtered = jobSource.socData.filter(job =>
        data.socCodes.includes(job.onetSOC)
      );
      return filtered;
    }

    return [];
  }, [artifact, contextJobs, wdaJobs, artifactWDA]);

  // Early return after ALL hooks
  if (!artifact) return null;

  const { artType, title, data = {}, identifier } = artifact;

  /* helper to shorten boilerplate */
  const footer = `Source: CTElink Assistant • ID: ${identifier}`;

  switch (artType) {
    /* ───────────── BAR CHART ───────────── */
    case "chart/bar":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
        >
          <FlexibleBarChart {...data} title={title} rawData={data} />
        </Card>
      );

    /* ───────────── LINE CHART ───────────── */
    case "chart/line":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
        >
          <FlexibleLineChart {...data} title={title} rawData={data} />
        </Card>
      );

    /* ───────────── EQUITY CHART ───────────── */
    case "chart/equity":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
        >
          <EquityNivoBarChart {...data} rawData={data} />
        </Card>
      );

    /* ───────────── JOBS TABLE ───────────── */
    case "table/jobs":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
          footer={footer}
        >
          <div className="space-y-4">
            {/* Jobs table with demand tooltips */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Occupation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Avg. Salary</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Growth</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Openings</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Demand</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.jobs?.slice(0, data.highlightTop || 10).map((job, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{job.title}</div>
                        <div className="text-xs text-gray-500">SOC: {job.soc}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        ${job.avgSalary?.toLocaleString() || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {job.growth !== undefined ? `${job.growth}%` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {job.openings?.toLocaleString() || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <DemandHoverTooltip onetSOC={job.soc} onetTitle={job.title}>
                          <div className="inline-block">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer
                              ${job.demandStatus === 1 ? 'bg-green-100 text-green-800' : 
                                job.demandStatus === 2 ? 'bg-gray-100 text-gray-800' : 
                                job.demandStatus === 3 ? 'bg-red-100 text-red-800' : 
                                'bg-gray-100 text-gray-500'}`}>
                              {job.demandLabel || 'No Data'}
                            </span>
                          </div>
                        </DemandHoverTooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Summary stats if available */}
            {data.summary && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{data.summary.totalOccupations}</div>
                  <div className="text-xs text-gray-500">Total Careers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    ${(data.summary.avgSalary || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">Avg. Salary</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{data.summary.inDemandCount || 0}</div>
                  <div className="text-xs text-gray-500">In Demand</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{data.summary.highWageCount || 0}</div>
                  <div className="text-xs text-gray-500">High Wage</div>
                </div>
              </div>
            )}
          </div>
        </Card>
      );

    /* ───────────── GENERIC DATA TABLE ───────────── */
    case "table/data":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
          footer={footer}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {data.headers?.map((h, idx) => (
                    <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.rows?.map((row, rIdx) => (
                  <tr key={rIdx} className="hover:bg-gray-50">
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {cell || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      );

    /* ───────────── GENDER DISTRIBUTION CHART ───────────── */
    case "chart/gender-distribution":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
          footer={footer}
        >
          {/* If you have a ConditionalGenderChartNivo component, use it here */}
          {/* Otherwise, render as a bar chart with gender-specific styling */}
          <FlexibleBarChart 
            {...data} 
            title={title} 
            rawData={data}
            colors={['#93c5fd', '#fbbf24', '#e879f9']} // Blue, Yellow, Purple for gender categories
          />
        </Card>
      );
      
    /* ───────────── WA LABOR MARKET TABLE ───────────── */
    case "table/wa-labor-market":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title || data.tableTitle || "Career Opportunities - District Programs"}
          footer={footer}
        >
          <div className="space-y-4">
            {/* Map the jobs array to the expected structure */}
            {resolvedJobs && resolvedJobs.length > 0 ? (
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    {/* Header */}
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '100px' }}>Demand / SOC</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ minWidth: '320px' }}>Job Title</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '110px' }}>Avg. Salary</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '90px' }}>Annual Openings ('21-'31)</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '80px' }}>Growth Rate</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '110px' }}>Employment</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase" style={{ width: '60px' }}>Links</th>
                      </tr>
                    </thead>
                    {/* Body */}
                    <tbody className="divide-y divide-gray-100">
                      {/* Group by SOC major code */}
                      {(() => {
                        const socGroups = {};
                        resolvedJobs.forEach(job => {
                          const socCode = job.soc || job.onetSOC || 'Unknown';
                          const majorCode = socCode.toString().substring(0, 2) || '99';
                          if (!socGroups[majorCode]) {
                            socGroups[majorCode] = [];
                          }
                          socGroups[majorCode].push(job);
                        });

                        return Object.entries(socGroups).sort(([a], [b]) => a.localeCompare(b)).map(([majorCode, jobs]) => {
                          const Icon = iconMapping[majorCode];

                          return (
                          <React.Fragment key={majorCode}>
                            {/* SOC Major Group Header */}
                            <tr className="bg-gray-50">
                              <td colSpan={7} className="px-3 py-2">
                                <div className="flex items-center gap-1.5">
                                  {Icon && <Icon className="w-4 h-4 text-gray-500" />}
                                  <span className="text-xs font-medium text-gray-700 uppercase">
                                    {shortMajorMapping[majorCode] || 'Other'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {/* Jobs in this education group */}
                            {jobs.map((job, idx) => {
                              // Transform the artifact job data
                              const socCode = job.soc || job.onetSOC || 'Unknown';
                              const jobTitle = job.title || job.onetTitle || 'Unknown Job Title';
                              const salary = job.avgSalary || job.avgAnnualWage || job.wage;
                              const openings = job.openings || job.avgTotalOpenings || job.totalOpenings;
                              const growth = job.growth !== undefined ? job.growth : job.longtermGrowth;
                              const employment = job.employment || job.estimatedEmployment;
                              const demand = job.demand || job.maxDemand || null;
                              
                              // Rating values (1-5 scale typically)
                              const wageRating = job.wageRating || null;
                              const openingsRating = job.openingsRating || job.totalOpeningsRating || null;
                              const growthRating = job.growthRating || null;
                              
                              // Handle case where job data is missing
                              const hasData = salary || openings || growth !== null || employment;
                              
                              // Helper to render rating bars
                              const RatingBars = ({ rating, color = 'bg-sky-300' }) => {
                                if (rating == null) return null;
                                return (
                                  <div className="h-2 flex mt-1">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <div
                                        key={i}
                                        className={`w-full h-2 border border-white ${
                                          rating > i ? color : 'bg-zinc-100'
                                        } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
                                      />
                                    ))}
                                  </div>
                                );
                              };
                              
                              return (
                                <tr key={`${socCode}-${idx}`} className="hover:bg-gray-50 transition-colors">
                                  {/* Demand indicator with CircularRating */}
                                  <td className="px-3 py-3 align-middle" style={{ width: '100px' }}>
                                    <div className="flex items-center justify-center">
                                      <DemandHoverTooltip
                                        onetSOC={socCode}
                                        onetTitle={jobTitle}
                                      >
                                        <div className='h-[60px] w-[40px] cursor-pointer flex items-center justify-center'>
                                          <CircularRating demand={demand} />
                                        </div>
                                      </DemandHoverTooltip>
                                      <span className="text-[10px] font-mono text-zinc-400 ml-2 whitespace-nowrap">
                                        {socCode !== 'Unknown' ? socCode.replace(/(\d{2})(\d{4})/, '$1-$2') : 'Unknown'}
                                      </span>
                                    </div>
                                  </td>
                                  {/* Job Title */}
                                  <td className="px-3 py-3" style={{ minWidth: '320px' }}>
                                    <div className="text-base font-semibold text-gray-900">
                                      {jobTitle}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {job.education || 'Education not specified'}
                                    </div>
                                  </td>
                                  {/* Salary with rating and hover tooltip */}
                                  <td className="px-2 py-2" style={{ width: '110px', maxWidth: '110px' }}>
                                    <div className="w-full max-w-full overflow-hidden">
                                      <div className="text-sm text-gray-700">
                                        {salary ? `$${salary.toLocaleString()}` : 'N/A'}
                                      </div>
                                      <div className="h-2 flex mt-1">
                                        {[...Array(5)].map((_, i) => (
                                          <div
                                            key={i}
                                            className={`w-full h-2 border border-white ${wageRating > i ? "bg-emerald-300" : "bg-zinc-100"} ${i === 0 ? "rounded-l" : i === 4 ? "rounded-r" : ""}`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                  {/* Openings with rating */}
                                  <td className="px-2 py-2" style={{ width: '90px' }}>
                                    <div>
                                      <div className="text-sm text-gray-700">
                                        {hasData && openings ? openings.toLocaleString() : 'N/A'}
                                      </div>
                                      <RatingBars rating={openingsRating} color="bg-sky-300" />
                                    </div>
                                  </td>
                                  {/* Growth Rate with rating */}
                                  <td className="px-2 py-2" style={{ width: '80px' }}>
                                    <div>
                                      <div className="text-sm text-gray-700">
                                        {hasData && growth !== null && growth !== undefined
                                          ? `${(growth * 100).toFixed(1)}%`
                                          : 'N/A'}
                                      </div>
                                      <RatingBars rating={growthRating} color="bg-sky-300" />
                                    </div>
                                  </td>
                                  {/* Employment (no rating) */}
                                  <td className="px-2 py-2 text-sm text-gray-700" style={{ width: '110px' }}>
                                    {hasData && employment ? employment.toLocaleString() : 'N/A'}
                                  </td>
                                  {/* Links Popover */}
                                  <td className="px-2 py-2 text-center" style={{ width: '60px' }}>
                                    {socCode !== 'Unknown' ? (
                                      <LinksPopover
                                        onetSOC={socCode}
                                        userDefaults={{
                                          defaultZip: '98101',
                                          defaultRadius: '50'
                                        }}
                                        linkBLS={job.linkBLS}
                                      />
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Footer note */}
                {data.tableSubtitle && (
                  <div className="px-4 py-3 bg-gray-50 border-t text-center">
                    <p className="text-xs text-gray-600">{data.tableSubtitle}</p>
                  </div>
                )}
              </div>
            ) : wdaLoading && data.socCodes?.length > 0 ? (
              /* Skeleton table: same headers + one row per SOC code to match final table height */
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '100px' }}>Demand / SOC</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ minWidth: '320px' }}>Job Title</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '110px' }}>Avg. Salary</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '90px' }}>Annual Openings</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '80px' }}>Growth Rate</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase" style={{ width: '110px' }}>Employment</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase" style={{ width: '60px' }}>Links</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.socCodes.map((soc, i) => (
                        <tr key={soc || i}>
                          <td className="px-3 py-3"><div className="h-4 w-14 bg-gray-200 rounded animate-pulse" /></td>
                          <td className="px-3 py-3" style={{ minWidth: '320px' }}>
                            <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mt-1.5" />
                          </td>
                          <td className="px-3 py-3"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-10 bg-gray-200 rounded animate-pulse" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-12 bg-gray-200 rounded animate-pulse" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-14 bg-gray-200 rounded animate-pulse" /></td>
                          <td className="px-3 py-3"><div className="h-4 w-6 mx-auto bg-gray-200 rounded animate-pulse" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No job data available for this program.</p>
                <p className="text-sm mt-2">Labor market data may not be mapped for this CIP code.</p>
              </div>
            )}

          </div>
        </Card>
      );

    /* ───────────── EQUITY GAP CHART ───────────── */
    case "chart/equity-gap":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
          footer={footer}
        >
          {/* Check if this is equity gap data with cipEquityData */}
          {data?.cipEquityData && Array.isArray(data.cipEquityData) ? (
            <EquityNivoBarChart {...data} rawData={data} />
          ) : (
            <FlexibleBarChart 
              {...data} 
              title={title} 
              rawData={data}
              colors={['#10b981', '#f59e0b', '#ef4444']} // Green, amber, red for equity levels
            />
          )}
        </Card>
      );

    /* ───────────── TEXT/MARKDOWN ───────────── */
    case "text/markdown":
    case "markdown":
    case "text/plain":
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title}
          footer={footer}
        >
          <div className="prose prose-sm max-w-none">
            <ImprovedMarkdownRenderer>
              {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
            </ImprovedMarkdownRenderer>
          </div>
        </Card>
      );

    /* ───────────── DEFAULT / UNKNOWN ───────────── */
    default:
      // Check if this looks like equity gap data (fallback)
      if (data?.cipEquityData && Array.isArray(data.cipEquityData)) {
        return (
          <Card
            color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
            title={title}
            footer={footer}
          >
            <EquityNivoBarChart {...data} rawData={data} />
          </Card>
        );
      }
      
      return (
        <Card
          color={{ bg: "bg-gradient-to-r from-stone-50 to-stone-100", text: "text-stone-800", border: "border-stone-200" }}
          title={title || artType}
          footer={footer}
        >
          <pre className="whitespace-pre-wrap text-sm overflow-x-auto">{JSON.stringify(data, null, 2)}</pre>
        </Card>
      );
  }
}